import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';
import { CleanContoursIcon, InterpolateIcon } from '~/assets/workspace-icons';
import { useRecoilState, useRecoilValue } from 'recoil';
import FloatingPanel from '~/components/toolbar/shared/FloatingPanel';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import {
    selectedROIState,
    selectedRoiIsPointTypeState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { openFloatingPanelState } from '~/state/atoms/toolsAtoms';
import ClearContoursInline from './slice/ClearContoursInline';
import InterpolationOperation from './slice/InterpolationOperation';

export default function SliceOperationsSection() {
    const selectedStructureSet = useRecoilValue(currentStructureSetState);
    const selectedRoiNumber = useRecoilValue(selectedROIState);
    const isPointRoi = useRecoilValue(selectedRoiIsPointTypeState);
    const primaryImage = useRecoilValue(primaryImageState);

    const isTilted = !!primaryImage?.firstSlice.tilted;

    const noRoiSelected =
        !selectedStructureSet ||
        selectedStructureSet.roiArray.length === 0 ||
        selectedRoiNumber === -1 ||
        isPointRoi;

    const selectedRoi = noRoiSelected
        ? undefined
        : selectedStructureSet.roiArray.find(
              (r) => r.roiNumber === selectedRoiNumber
          );

    // Clear and interpolate modify the selected ROI in place, so either an
    // approved structure set or an approved ROI blocks the operation.
    const isApproved =
        !!selectedStructureSet?.isApproved || !!selectedRoi?.isApproved;

    const isDisabled = noRoiSelected || isTilted || isApproved;

    const clearAnchorRef = useRef<HTMLElement | null>(null);
    const interpolateAnchorRef = useRef<HTMLElement | null>(null);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    useEffect(() => {
        if (
            isDisabled &&
            (openPanel === 'interpolation' || openPanel === 'clear')
        ) {
            setOpenPanel(null);
        }
    }, [isDisabled, openPanel, setOpenPanel]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<InterpolateIcon />}
                tooltip="Interpolation"
                tooltipPlacement="left"
                disabled={isDisabled}
                panelId="interpolation"
                anchorRef={interpolateAnchorRef}
            />
            <FloatingPanel
                panelId="interpolation"
                anchorEl={interpolateAnchorRef.current}
                placement="left-start"
            >
                <InterpolationOperation />
            </FloatingPanel>
            <ToolbarButton
                icon={<CleanContoursIcon />}
                tooltip="Clear Contours"
                tooltipPlacement="left"
                disabled={isDisabled}
                panelId="clear"
                anchorRef={clearAnchorRef}
            />
            <FloatingPanel
                panelId="clear"
                anchorEl={clearAnchorRef.current}
                placement="left-start"
            >
                <ClearContoursInline />
            </FloatingPanel>
        </Box>
    );
}
